import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Save, Loader2, Pencil, X, Palette, Quote, Megaphone, Smile } from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";

const TONE_OPTIONS = [
  { value: "formal", label: "Formal" },
  { value: "informal", label: "Informal" },
  { value: "tecnico", label: "Técnico" },
  { value: "amigavel", label: "Amigável" },
  { value: "descontraido", label: "Descontraído" },
] as const;

type ToneValue = typeof TONE_OPTIONS[number]["value"];

const toneLabel = (v: string) => TONE_OPTIONS.find((t) => t.value === v)?.label ?? v;

export function BrandVoicePanel() {
  const [editing, setEditing] = useState(false);
  const [brandName, setBrandName] = useState("");
  const [toneOfVoice, setToneOfVoice] = useState<ToneValue>("formal");
  const [wordsToUse, setWordsToUse] = useState("");
  const [wordsToAvoid, setWordsToAvoid] = useState("");
  const [slogan, setSlogan] = useState("");
  const [catchPhrases, setCatchPhrases] = useState("");
  const [emojis, setEmojis] = useState("");
  const [sampleText, setSampleText] = useState("");
  const [defaultSignature, setDefaultSignature] = useState("");

  const { data: existing, isLoading } = trpc.aiEmail.getBrandVoice.useQuery();
  const utils = trpc.useUtils();

  const upsertMutation = trpc.aiEmail.upsertBrandVoice.useMutation({
    onSuccess: () => {
      utils.aiEmail.getBrandVoice.invalidate();
      toast.success("Voz da marca salva!");
      setEditing(false);
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao salvar voz da marca")),
  });

  function hydrateFromExisting() {
    if (!existing) return;
    setBrandName(existing.brandName);
    setToneOfVoice(existing.toneOfVoice as ToneValue);
    setWordsToUse((existing.wordsToUse as string[] | null)?.join(", ") ?? "");
    setWordsToAvoid((existing.wordsToAvoid as string[] | null)?.join(", ") ?? "");
    setSlogan((existing as { slogan?: string | null }).slogan ?? "");
    setCatchPhrases(((existing as { catchPhrases?: string[] | null }).catchPhrases ?? []).join("\n"));
    setEmojis(((existing as { emojis?: string[] | null }).emojis ?? []).join(" "));
    setSampleText(existing.sampleText ?? "");
    setDefaultSignature(existing.defaultSignature ?? "");
  }

  useEffect(() => {
    hydrateFromExisting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing]);

  function handleSave() {
    const csvToArray = (s: string) => s.split(",").map((w) => w.trim()).filter(Boolean);
    const linesToArray = (s: string) => s.split("\n").map((w) => w.trim()).filter(Boolean);
    const emojisList = (s: string) => s.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean);

    upsertMutation.mutate({
      brandName,
      toneOfVoice,
      wordsToUse: csvToArray(wordsToUse).length ? csvToArray(wordsToUse) : undefined,
      wordsToAvoid: csvToArray(wordsToAvoid).length ? csvToArray(wordsToAvoid) : undefined,
      slogan: slogan.trim() || null,
      catchPhrases: linesToArray(catchPhrases).length ? linesToArray(catchPhrases) : undefined,
      emojis: emojisList(emojis).length ? emojisList(emojis) : undefined,
      sampleText: sampleText || undefined,
      defaultSignature: defaultSignature || undefined,
    });
  }

  function handleCancel() {
    hydrateFromExisting();
    setEditing(false);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const wordsUseList = (existing?.wordsToUse as string[] | null) ?? [];
  const wordsAvoidList = (existing?.wordsToAvoid as string[] | null) ?? [];
  const sloganValue = (existing as { slogan?: string | null } | undefined)?.slogan ?? null;
  const catchPhrasesList = ((existing as { catchPhrases?: string[] | null } | undefined)?.catchPhrases) ?? [];
  const emojisList = ((existing as { emojis?: string[] | null } | undefined)?.emojis) ?? [];
  const showView = !editing && existing && existing.brandName;

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Voz da Marca</h2>
          <p className="text-sm text-muted-foreground">
            Define a identidade verbal aplicada em todos os textos gerados pela IA (e-mail, WhatsApp e atendimento).
          </p>
        </div>
        {showView && (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
            <Pencil className="w-3.5 h-3.5 mr-1.5" /> Editar
          </Button>
        )}
      </div>

      {showView ? (
        <Card className="overflow-hidden">
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50/50 dark:from-indigo-950/30 dark:to-purple-950/20 border-b px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-white shadow-sm">
                <Palette className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Marca</p>
                <h3 className="text-xl font-semibold">{existing!.brandName}</h3>
              </div>
              <div className="ml-auto">
                <Badge variant="secondary" className="text-xs">
                  Tom: {toneLabel(existing!.toneOfVoice)}
                </Badge>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-5">
            {sloganValue && (
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                  <Megaphone className="w-3.5 h-3.5" /> Slogan
                </Label>
                <p className="mt-1.5 text-base font-medium text-indigo-700 dark:text-indigo-400">
                  {sloganValue}
                </p>
              </div>
            )}

            {catchPhrasesList.length > 0 && (
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Frases de efeito
                </Label>
                <div className="mt-2 space-y-1.5">
                  {catchPhrasesList.map((p, i) => (
                    <div key={i} className="text-sm bg-indigo-50/60 dark:bg-indigo-950/20 rounded px-3 py-1.5 italic text-foreground/80">
                      "{p}"
                    </div>
                  ))}
                </div>
              </div>
            )}

            {emojisList.length > 0 && (
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                  <Smile className="w-3.5 h-3.5" /> Emojis da marca
                </Label>
                <div className="mt-2 flex flex-wrap gap-1.5 text-2xl">
                  {emojisList.map((e, i) => (
                    <span key={i} className="bg-muted/50 rounded px-2 py-0.5">{e}</span>
                  ))}
                </div>
              </div>
            )}

            {wordsUseList.length > 0 && (
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Palavras a usar
                </Label>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {wordsUseList.map((w, i) => (
                    <Badge key={i} variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300">
                      {w}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {wordsAvoidList.length > 0 && (
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Palavras a evitar
                </Label>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {wordsAvoidList.map((w, i) => (
                    <Badge key={i} variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-300">
                      {w}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {existing!.sampleText && (
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Exemplo aprovado
                </Label>
                <div className="mt-2 relative pl-4 border-l-2 border-indigo-200">
                  <Quote className="w-3.5 h-3.5 text-indigo-300 absolute -left-[7px] top-0 bg-background" />
                  <p className="text-sm italic text-muted-foreground leading-relaxed whitespace-pre-wrap">
                    {existing!.sampleText}
                  </p>
                </div>
              </div>
            )}

            {existing!.defaultSignature && (
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Assinatura padrão
                </Label>
                <pre className="mt-2 text-sm font-sans text-foreground/80 whitespace-pre-wrap bg-muted/30 rounded p-3">
                  {existing!.defaultSignature}
                </pre>
              </div>
            )}

            {wordsUseList.length === 0 && wordsAvoidList.length === 0 && !existing!.sampleText && !existing!.defaultSignature && !sloganValue && catchPhrasesList.length === 0 && emojisList.length === 0 && (
              <p className="text-sm text-muted-foreground italic">
                Nenhum detalhe adicional configurado. Clique em Editar ou analise um documento para enriquecer a voz da marca.
              </p>
            )}
          </div>
        </Card>
      ) : (
        <Card className="p-6 space-y-5">
          {existing && (
            <div className="flex items-center justify-between -mt-1 mb-2">
              <p className="text-xs text-muted-foreground">
                {existing.brandName ? "Editando voz da marca" : "Configurando voz da marca pela primeira vez"}
              </p>
              {existing.brandName && (
                <Button size="sm" variant="ghost" onClick={handleCancel}>
                  <X className="w-3.5 h-3.5 mr-1.5" /> Cancelar
                </Button>
              )}
            </div>
          )}

          <div>
            <Label>Nome da Marca *</Label>
            <Input value={brandName} onChange={(e) => setBrandName(e.target.value)} placeholder="Ex: MinhaMarca" />
          </div>

          <div>
            <Label>Tom de Voz</Label>
            <Select value={toneOfVoice} onValueChange={(v) => setToneOfVoice(v as ToneValue)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TONE_OPTIONS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">Define como a IA se comunica em nome da marca</p>
          </div>

          <div>
            <Label>Palavras a Usar (separadas por vírgula)</Label>
            <Textarea
              value={wordsToUse}
              onChange={(e) => setWordsToUse(e.target.value)}
              placeholder="inovação, parceria, resultado, transformação"
              rows={2}
            />
            <p className="text-xs text-muted-foreground mt-1">Termos que reforçam a identidade da marca</p>
          </div>

          <div>
            <Label>Palavras a Evitar (separadas por vírgula)</Label>
            <Textarea
              value={wordsToAvoid}
              onChange={(e) => setWordsToAvoid(e.target.value)}
              placeholder="grátis, barato, spam, urgente"
              rows={2}
            />
            <p className="text-xs text-muted-foreground mt-1">Termos que desalinham ou prejudicam deliverability</p>
          </div>

          <div>
            <Label className="flex items-center gap-1.5">
              <Megaphone className="w-3.5 h-3.5" /> Slogan
            </Label>
            <Input
              value={slogan}
              onChange={(e) => setSlogan(e.target.value)}
              placeholder="Ex: Somente o conhecimento te leva avante"
            />
            <p className="text-xs text-muted-foreground mt-1">Frase principal da marca, se houver</p>
          </div>

          <div>
            <Label>Frases de Efeito (uma por linha)</Label>
            <Textarea
              value={catchPhrases}
              onChange={(e) => setCatchPhrases(e.target.value)}
              placeholder="Domine a Lei 14.133/21&#10;Transforme sua carreira&#10;Conhecimento que leva avante"
              rows={3}
            />
            <p className="text-xs text-muted-foreground mt-1">Bordões e frases recorrentes que reforçam a identidade</p>
          </div>

          <div>
            <Label className="flex items-center gap-1.5">
              <Smile className="w-3.5 h-3.5" /> Emojis da Marca
            </Label>
            <Input
              value={emojis}
              onChange={(e) => setEmojis(e.target.value)}
              placeholder="🚀 💡 ✨ 📈"
            />
            <p className="text-xs text-muted-foreground mt-1">Separados por espaço — usados em e-mails e WhatsApp</p>
          </div>

          <div>
            <Label>Exemplo de Texto Aprovado</Label>
            <Textarea
              value={sampleText}
              onChange={(e) => setSampleText(e.target.value)}
              placeholder="Cole aqui um texto que represente bem a comunicação da sua marca..."
              rows={4}
            />
            <p className="text-xs text-muted-foreground mt-1">A IA usará como referência de estilo</p>
          </div>

          <div>
            <Label>Assinatura Padrão</Label>
            <Textarea
              value={defaultSignature}
              onChange={(e) => setDefaultSignature(e.target.value)}
              placeholder="Equipe MinhaMarca&#10;www.minhamarca.com.br"
              rows={2}
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button onClick={handleSave} disabled={!brandName.trim() || upsertMutation.isPending} className="flex-1">
              {upsertMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Salvar Voz da Marca
            </Button>
            {existing?.brandName && (
              <Button variant="outline" onClick={handleCancel}>
                Cancelar
              </Button>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
